import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import classes from './TreeMenu.module.scss';
import { IconsEnum } from '../../../../constants/icons';
import { DropOptions, NodeModel, Tree } from '@minoru/react-dnd-treeview';
import { TreeMenuModalTypeEnums } from '../../../../constants/find-content';
import { TreeMenuModals } from '../TreeMenuModals';
import {
  CoreImportFolderFieldsFragment,
  ImportSourceFieldsFragment,
  SocialOAuthProvider
} from '../../../../models/graphql';
import { TreeMenuItemBaseType, TreeMenuItemType, TreeType } from '../../../../models/find-content';
import { TreeItem } from '../TreeMenuItem';
import { TreeMenuItemPreview } from '../TreeMenuItemPreview';
import { OpmlImportModal } from '../OpmlImportModal';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';
import classNames from 'classnames';

export type TreeMenuViewProps = {
  treeMenuItems: TreeType[];
  updateMenuItem: (itemId: string, parentId: string, position: number, isSourceType?: boolean) => void;
  setActionCondition: (modalType: TreeMenuModalTypeEnums | undefined, item: undefined) => void;
  showModalType?: TreeMenuModalTypeEnums | undefined;
  selectedMenuItem: CoreImportFolderFieldsFragment | ImportSourceFieldsFragment | undefined;
  reFetch: CallableFunction;
};

export const TreeMenuView = (props: TreeMenuViewProps): JSX.Element => {
  const { treeMenuItems, updateMenuItem, showModalType, setActionCondition } = props;
  const [treeData, setTreeData] = useState<NodeModel<TreeMenuItemType>[]>([]);
  const [showOpmlModal, setShowOpmlModal] = useState<boolean>(false);
  const [draggedSourceType, setDraggedSourceType] = useState<TreeMenuItemBaseType['type'] | undefined>(undefined);

  const handleDrop = (newTree: NodeModel<TreeMenuItemType>[], options: DropOptions) => {
    const { dragSourceId, dropTargetId, relativeIndex, dragSource } = options;

    // relativeIndex is 0 base, need to change relativeIndex to Position which is 1 base
    const correctedIndex = Number(relativeIndex) + 1;
    const refSource = dragSource?.data as TreeMenuItemType; // NodeModel is defined by 3rd party as unknown, so we must cast
    const isSourceType = refSource.type === DraggableTypesObject.MENU_TREE_SOURCE;

    updateMenuItem(String(dragSourceId), String(dropTargetId), Number(correctedIndex), isSourceType);
    setTreeData(newTree);
  };

  const actionNodes: TreeMenuItemType[] = useMemo(
    () => [
      {
        id: '1',
        type: DraggableTypesObject.MENU_TREE_ACTION,
        label: 'OPML Import',
        icon: IconsEnum.import,
        action: () => setShowOpmlModal(true),
        hasMenu: false
      },
      {
        id: '0',
        type: DraggableTypesObject.MENU_TREE_ACTION,
        label: 'Add Folder',
        icon: IconsEnum.plus,
        action: () => setActionCondition(TreeMenuModalTypeEnums.ADD_FOLDER, undefined),
        hasMenu: false
      }
    ],
    [setActionCondition]
  );

  const itemsCount = props.treeMenuItems.length + 1;
  const treeWrapperClasses = classNames('margin-top-small-16 margin-bottom-small-8', classes.TreeMenuWrapper);
  const treeMenuHeightStyle: CSSProperties = { height: `${itemsCount * 40}px` };

  useEffect(() => {
    setTreeData(treeMenuItems);
  }, [treeMenuItems]);

  return (
    <div className={classes.TreeMenu} style={treeMenuHeightStyle}>
      <div className={classes.ActionsContainer}>
        {actionNodes.map((item, idx) => {
          return (
            <TreeItem
              key={`${idx}`}
              item={item}
              depth={0}
              isOpen={false}
              onToggle={() => {
                // ...
              }}
              hasDivider={idx === 0}
            />
          );
        })}
      </div>
      <div className={treeWrapperClasses}>
        <Tree
          tree={treeData}
          rootId={'0'}
          classes={{
            root: classes.RootLevelList,
            dropTarget: draggedSourceType === DraggableTypesObject.MENU_TREE_SOURCE ? classes.DropTarget : undefined,
            draggingSource: classes.TreeMenuItemDragging
          }}
          sort={false}
          dropTargetOffset={10}
          enableAnimateExpand={true}
          render={(node, { depth, isOpen, onToggle }) => {
            if (
              node.data &&
              node.data.type === DraggableTypesObject.MENU_TREE_SOURCE &&
              'provider' in node.data &&
              node.data.provider === SocialOAuthProvider.TWITTER
            ) {
              return <></>;
            }

            if (!node.data) {
              return <></>;
            }

            return <TreeItem item={node.data} depth={depth} isOpen={isOpen} onToggle={onToggle} />;
          }}
          placeholderRender={() => <div className={classes.PlaceHolder} />}
          dragPreviewRender={monitorProps => {
            if (!monitorProps.item.data) {
              return <></>;
            }

            return <TreeMenuItemPreview {...monitorProps.item.data} />;
          }}
          canDrop={(tree, { dragSource, dropTargetId }): boolean => {
            setDraggedSourceType(dragSource?.data?.type || undefined);

            if (dragSource?.parent === dropTargetId) {
              return true;
            }

            const targetType = tree.find(t => t.id === dropTargetId);
            // Cannot drop on same type
            return targetType?.data?.type !== dragSource?.data?.type;
          }}
          onDrop={handleDrop}
        />
      </div>
      {showModalType && <TreeMenuModals />}
      {showOpmlModal && <OpmlImportModal closeHandler={() => setShowOpmlModal(false)} />}
    </div>
  );
};
